import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Target, Brain, ArrowRight, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  ChevronDown, X,
  MessageCircle, Sparkles
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'F1-1'

// ── 스토리텔링 인트로 데이터 ─────────────────────────────────────────────────
interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  FA01: {
    scene: '🚗 도로 위. 지호가 자동차 속도계를 보면서 이야기한다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '엄마, 속도계가 60이면 1시간에 60km를 간다는 거죠? 그럼 2시간이면 120km?' },
      { speaker: 'teacher', emoji: '👩', name: '엄마', text: '맞아! 시간이 2배가 되면 거리도 2배가 되지. 이게 바로 정비례 관계야.' },
      { speaker: 'friend', emoji: '😮', name: '수아', text: '그러면 속도가 일정할 때 거리와 시간이 정비례한다는 건가요?' },
      { speaker: 'teacher', emoji: '👩', name: '엄마', text: '정확해! y=ax에서 a가 속도, x가 시간, y가 거리야. x가 커질수록 y도 일정한 비율로 커져.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '함수: x값 하나에 y값 하나가 대응 | 정비례: y=ax (a≠0) | y/x = a (일정)' },
    ],
    reveal: 'y=ax: x가 2배 → y도 2배 | y÷x = a (항상 일정)',
    revealEmoji: '📈',
  },
  FA02: {
    scene: '🌱 과학 수업. 식물 성장 실험 데이터를 분석 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '선생님, 비료를 2배 주면 식물이 2배로 자라나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '실험해봤더니 그래! 비료량 x(g)와 식물 키 y(cm)가 y=3x로 정비례했어.' },
      { speaker: 'friend', emoji: '🤔', name: '수아', text: '그 그래프를 그리면 어떻게 생겼어요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '원점(0,0)을 지나는 직선이에요! a>0이면 오른쪽 위로, a<0이면 오른쪽 아래로 향해요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: 'y=ax 그래프: 원점 통과 직선 | a>0: 1·3사분면 | a<0: 2·4사분면 | |a| 클수록 y축에 가까움' },
    ],
    reveal: 'y=ax: 원점 지나는 직선 | a>0→오른위 | a<0→오른아래',
    revealEmoji: '📉',
  },
  FA03: {
    scene: '🎸 음악 스튜디오. 음파 진동수와 파장을 연구 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '소리의 속도는 일정한데 진동수가 높아지면 파장이 짧아진다고요?' },
      { speaker: 'teacher', emoji: '🎵', name: '음향사', text: '맞아요! 속도=진동수×파장이니까, 속도 v가 고정되면 진동수 f와 파장 λ는 v=fλ, 즉 λ=v/f예요.' },
      { speaker: 'friend', emoji: '😄', name: '수아', text: '그러면 진동수가 2배면 파장은 절반! 이게 반비례 관계인가요?' },
      { speaker: 'teacher', emoji: '🎵', name: '음향사', text: '정확해요! y=a/x에서 x와 y의 곱 xy=a로 항상 일정해요. 이게 반비례의 핵심!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '반비례: y=a/x | xy=a (일정) | x가 2배→y가 절반 | x가 3배→y가 1/3' },
    ],
    reveal: 'y=a/x: xy=a 항상 일정 | x×y=상수',
    revealEmoji: '🔄',
  },
  FA04: {
    scene: '🏗️ 공사 현장. 작업 인원과 완공 일수를 계획 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '공사 인원을 2배로 늘리면 공사 기간이 절반으로 줄어드나요?' },
      { speaker: 'teacher', emoji: '👷', name: '현장소장', text: '이론적으로는 그래요! 인원 x명이 하루 작업량이 같다면 기간 y=a/x로 반비례해요.' },
      { speaker: 'friend', emoji: '🤔', name: '수아', text: '반비례 그래프는 어떻게 생겼어요? 쌍곡선이라고 했는데...' },
      { speaker: 'teacher', emoji: '👷', name: '현장소장', text: '쌍곡선이에요! 원점 대칭이고 축에 한없이 가까워지지만 닿지 않아요. a>0이면 1·3사분면에 있어요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: 'y=a/x 그래프: 쌍곡선 | 원점 대칭 | a>0: 1·3사분면 | a<0: 2·4사분면 | 축에 점근' },
    ],
    reveal: 'y=a/x: 쌍곡선 | a>0→1,3사분면 | 원점 대칭 | 축에 점근',
    revealEmoji: '∞',
  },
}

// ── 학습 단계 정의 ───────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'FA01',
    title: '함수의 뜻과 정비례 관계',
    subtitle: '함수의 개념을 이해하고 정비례 관계 y=ax를 파악한다',
    icon: '📈',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    textColor: 'text-blue-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['함수', '정비례', 'y=ax', '대응'],
    objective: '함수의 개념(x값 하나에 y값 하나 대응)을 이해하고 정비례 y=ax에서 y÷x=a(일정)임을 설명할 수 있다.',
    concept: `**함수의 뜻과 정비례 관계**

📌 함수란?
• x의 값이 변함에 따라 y의 값이 하나씩 정해지는 관계
• x → y: "x에 대한 y의 함수"
• 예: 거리=속도×시간 에서 속도가 일정할 때 시간(x)에 따라 거리(y)가 하나로 결정됨

📌 정비례 관계 y=ax
• x와 y의 관계: y = ax  (a ≠ 0, a는 상수)
• 비례상수 a = y ÷ x (항상 일정!)
• x가 2배 → y도 2배
• x가 3배 → y도 3배
• x가 1/2배 → y도 1/2배

📌 정비례 관계 판별
| x | 1 | 2 | 3 | 4 |
|---|---|---|---|---|
| y | 3 | 6 | 9 | 12 |
→ y/x = 3 (항상 일정) → y = 3x (정비례!)

📌 정비례 관계 예시
• 속도 60km/h일 때: 거리 y = 60x
• 사탕 한 개에 200원: 금액 y = 200x
• 1분에 3L씩 채우는 물통: y = 3x

⚠️ 주의
• y=ax+b (b≠0)이면 정비례가 아님!
• 반드시 원점(0,0)을 통과해야 정비례`,
    keyPoints: [
      '함수: x값 하나에 y값 하나가 대응',
      '정비례: y = ax (a≠0)',
      'y÷x = a : 항상 일정',
      'x가 n배 → y도 n배',
    ],
    examples: [
      {
        question: '다음 중 y가 x에 대한 함수인 것을 고르고,\n정비례 관계인 것을 구하시오.\n① y = 2x  ② y = x + 3  ③ y = x²  ④ y = 5x',
        answer: '함수: ①②③④ 모두 / 정비례: ①④',
        explanation: '① y=2x: y/x=2 (일정) → 정비례 ✓\n② y=x+3: x=1→y=4, y/x=4; x=2→y=5, y/x=2.5 (불일정) → 정비례 아님\n③ y=x²: x=1→y=1, x=2→y=4 (2배가 아닌 4배) → 정비례 아님\n④ y=5x: y/x=5 (일정) → 정비례 ✓',
      },
      {
        question: 'x와 y가 정비례하고 x=3일 때 y=12이다.\n① 비례상수 a를 구하시오.\n② y=x 관계식을 쓰시오.\n③ x=7일 때 y의 값을 구하시오.',
        answer: '① a=4  ② y=4x  ③ y=28',
        explanation: '① a = y÷x = 12÷3 = 4\n② y = 4x\n③ y = 4×7 = 28',
      },
    ],
    practice: {
      question: '물을 1분에 5L씩 수영장에 채운다.\nx분 후 물의 양을 y(L)라 할 때,\n① y를 x의 식으로 나타내시오.\n② 15분 후 물의 양을 구하시오.\n③ 물의 양이 200L가 되는 시간을 구하시오.',
      answer: '① y=5x  ② 75L  ③ 40분',
      hint: '1분에 5L씩이면 x분에 5x L. ③는 5x=200 방정식 풀기.',
      steps: [
        '① y = 5 × x = 5x',
        '② x=15: y = 5×15 = 75 (L)',
        '③ 5x = 200 → x = 40 (분)',
      ],
    },
    misconceptions: ['FP01'],
  },
  {
    id: 'FA02',
    title: '정비례 그래프',
    subtitle: '정비례 y=ax의 그래프(원점 통과 직선)를 이해하고 그린다',
    icon: '📉',
    color: 'bg-sky-500',
    lightColor: 'bg-sky-50',
    borderColor: 'border-sky-300',
    textColor: 'text-sky-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['정비례', '그래프', '직선', '원점'],
    objective: 'y=ax 그래프가 원점을 지나는 직선임을 이해하고 a의 부호와 크기에 따른 그래프 모양을 설명할 수 있다.',
    concept: `**정비례 그래프**

📌 y=ax 그래프의 특징
• 원점(0, 0)을 지나는 직선
• 직선의 기울기 = a (비례상수)

📌 a의 부호에 따른 위치
| a의 부호 | 그래프 위치 | 방향 |
|---------|-----------|------|
| a > 0 | 1사분면·3사분면 | 오른쪽 위 ↗ |
| a < 0 | 2사분면·4사분면 | 오른쪽 아래 ↘ |

📌 a의 절댓값과 기울기
• |a|가 클수록 → y축에 가깝게 (가파름)
• |a|가 작을수록 → x축에 가깝게 (완만함)

📌 그래프 그리는 방법
① 원점 (0, 0) 찍기
② x=1일 때 y=a인 점 (1, a) 찍기
③ 두 점을 직선으로 연결

📌 y=2x vs y=3x vs y=½x 비교
• y=3x가 y=2x보다 y축에 더 가까움 (|3|>|2|)
• y=½x는 y=2x보다 x축에 더 가까움 (|½|<|2|)

📌 정비례 그래프에서 a 구하기
• 그래프가 점 (2, 6)을 지남 → y=ax에 대입: 6=2a → a=3
• y=3x

⚠️ 주의
• y=ax+b (b≠0)은 원점을 지나지 않음 → 정비례 그래프 아님!`,
    keyPoints: [
      'y=ax: 원점(0,0)을 지나는 직선',
      'a>0: 1·3사분면, 오른쪽 위로',
      'a<0: 2·4사분면, 오른쪽 아래로',
      '|a| 클수록 y축에 가까움(가파름)',
    ],
    examples: [
      {
        question: 'y=ax 그래프가 점 (-2, 6)을 지날 때\n① a의 값을 구하시오.\n② 이 그래프는 몇 사분면을 지나는가?',
        answer: '① a=-3  ② 2사분면·4사분면',
        explanation: '① 6 = a×(-2) → a = 6÷(-2) = -3\n② a=-3<0이므로 2사분면·4사분면 통과\n(오른쪽 아래 방향의 직선)',
      },
      {
        question: '다음 y=ax 그래프 중 x축에 가장 가까운 것은?\n① y=3x  ② y=-2x  ③ y=½x  ④ y=-4x',
        answer: '③ y=½x',
        explanation: '|a| 값: ①3  ②2  ③0.5  ④4\n|a|가 가장 작은 ③ y=½x가 x축에 가장 가까움',
      },
    ],
    practice: {
      question: 'y=ax 그래프가 점 (3, -9)를 지날 때\n① a의 값을 구하시오.\n② 이 그래프가 지나는 사분면을 쓰시오.\n③ x=5일 때 y의 값을 구하시오.',
      answer: '① a=-3  ② 2·4사분면  ③ y=-15',
      hint: '① y=ax에 x=3, y=-9 대입. ② a의 부호 확인.',
      steps: [
        '① y=ax에 (3,-9) 대입: -9=3a → a=-3',
        '② a=-3<0이므로 2사분면·4사분면',
        '③ y=-3×5=-15',
      ],
    },
    misconceptions: ['FP01'],
  },
  {
    id: 'FA03',
    title: '반비례 관계',
    subtitle: '반비례 관계 y=a/x를 이해하고 xy=a(일정)을 활용한다',
    icon: '🔄',
    color: 'bg-violet-500',
    lightColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
    textColor: 'text-violet-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['반비례', 'y=a/x', 'xy=상수'],
    objective: '반비례 관계 y=a/x에서 xy=a(일정)임을 이해하고 비례상수를 구해 관계식을 세울 수 있다.',
    concept: `**반비례 관계**

📌 반비례 관계란?
• y = a/x  (a ≠ 0)
• x와 y의 곱이 항상 일정: xy = a
• x가 2배 → y는 1/2배
• x가 3배 → y는 1/3배

📌 반비례 관계 판별
| x | 1 | 2 | 3 | 4 |
|---|---|---|---|---|
| y | 12 | 6 | 4 | 3 |
→ xy = 12 (항상 일정) → y = 12/x (반비례!)

📌 정비례 vs 반비례 비교
| 구분 | 정비례 | 반비례 |
|------|--------|--------|
| 식 | y=ax | y=a/x |
| 특징 | y/x=a | xy=a |
| x↑ | y↑ | y↓ |

📌 반비례 관계 실생활 예시
• 일정 거리를 달릴 때: 속도×시간=거리(일정) → 속도와 시간이 반비례
• 직사각형 넓이 고정: 가로×세로=일정 → 가로와 세로가 반비례
• 일정 인원으로 일할 때: 인원×기간=일량(일정) → 인원과 기간이 반비례

📌 비례상수 a 구하기
• x=2, y=9이고 반비례: a = xy = 2×9 = 18
• 따라서 y = 18/x

⚠️ 주의
• x=0일 때는 y가 정의되지 않음 (분모가 0이 될 수 없음)
• y=a/x는 x≠0 조건 필수!`,
    keyPoints: [
      '반비례: y = a/x (a≠0)',
      'xy = a : 항상 일정',
      'x가 n배 → y는 1/n배',
      'x=0일 때 y는 정의 안 됨',
    ],
    examples: [
      {
        question: 'x와 y가 반비례하고 x=4일 때 y=6이다.\n① 비례상수 a를 구하시오.\n② y를 x의 식으로 나타내시오.\n③ x=8일 때 y의 값을 구하시오.',
        answer: '① a=24  ② y=24/x  ③ y=3',
        explanation: '① a = xy = 4×6 = 24\n② y = 24/x\n③ y = 24/8 = 3',
      },
      {
        question: '달리는 거리가 120km로 일정할 때\n속도 x(km/h)와 시간 y(h)의 관계를 구하고\n속도가 60km/h일 때의 시간을 구하시오.',
        answer: 'y=120/x, 2시간',
        explanation: '속도×시간=거리(일정): xy=120 → y=120/x\nx=60: y=120/60=2 (h)',
      },
    ],
    practice: {
      question: '직사각형의 넓이가 36cm²로 고정되어 있다.\n가로의 길이를 x(cm), 세로의 길이를 y(cm)라 할 때\n① y를 x의 식으로 나타내시오.\n② x=4일 때 y를 구하시오.\n③ y=12일 때 x를 구하시오.',
      answer: '① y=36/x  ② y=9  ③ x=3',
      hint: '넓이=가로×세로 → xy=36 → y=36/x',
      steps: [
        '① xy=36 → y=36/x',
        '② x=4: y=36/4=9 (cm)',
        '③ y=12: 12=36/x → x=36/12=3 (cm)',
      ],
    },
    misconceptions: ['FP02'],
  },
  {
    id: 'FA04',
    title: '반비례 그래프와 활용',
    subtitle: '반비례 y=a/x의 그래프(쌍곡선)와 정비례·반비례 활용 문제를 해결한다',
    icon: '∞',
    color: 'bg-rose-500',
    lightColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
    textColor: 'text-rose-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['반비례', '쌍곡선', '그래프', '활용'],
    objective: 'y=a/x 그래프가 쌍곡선임을 이해하고 a의 부호·크기에 따른 그래프 특성을 설명하며 활용 문제를 풀 수 있다.',
    concept: `**반비례 그래프와 활용**

📌 y=a/x 그래프의 특징 (쌍곡선)
• 원점에 대해 대칭인 곡선
• x축·y축에 한없이 가까워지지만 닿지 않음 (점근선)
• x=0에서 정의되지 않음

📌 a의 부호에 따른 위치
| a의 부호 | 그래프 위치 |
|---------|-----------|
| a > 0 | 1사분면·3사분면 |
| a < 0 | 2사분면·4사분면 |

📌 |a|의 크기와 그래프
• |a|가 클수록 → 원점에서 멀어짐 (곡선이 바깥쪽)
• |a|가 작을수록 → 원점에 가까워짐

📌 정비례 vs 반비례 그래프 비교
| | y=ax | y=a/x |
|--|------|-------|
| 모양 | 직선 | 쌍곡선 |
| 원점 통과 | ✓ | ✗ |
| 축과 교점 | 원점 | 없음 |

📌 활용 문제 풀이 전략
1. 두 양의 관계 파악 (정비례인가? 반비례인가?)
2. x가 증가할 때 y가 증가(정비례) / 감소(반비례) 확인
3. 비례상수 a 구하기
4. 관계식 y=ax 또는 y=a/x 세우기
5. 구하는 값 계산

⚠️ 혼동 주의
• 정비례: y=ax → y/x=a(일정)
• 반비례: y=a/x → xy=a(일정)
• 둘을 혼동하지 않도록!`,
    keyPoints: [
      'y=a/x: 쌍곡선, 원점에 대해 대칭',
      'a>0: 1·3사분면, a<0: 2·4사분면',
      '축에 한없이 가까워지나 교점 없음',
      '활용: 정비례(y=ax) vs 반비례(y=a/x) 판별',
    ],
    examples: [
      {
        question: 'y=a/x 그래프가 점 (3, -4)를 지날 때\n① a의 값을 구하시오.\n② 이 그래프가 지나는 사분면을 쓰시오.\n③ y=1일 때 x의 값을 구하시오.',
        answer: '① a=-12  ② 2·4사분면  ③ x=-12',
        explanation: '① a=xy=3×(-4)=-12\n② a=-12<0이므로 2사분면·4사분면\n③ 1=-12/x → x=-12',
      },
      {
        question: '10명이 15일에 끝낼 수 있는 일을\n6일 안에 끝내려면 몇 명이 필요한가?\n(단, 각자 작업량은 동일)',
        answer: '25명',
        explanation: '인원×일수=일정: 10×15=150\ny=150/x (반비례)\nx=6: y=150/6=25 (명)',
      },
    ],
    practice: {
      question: '기어 A(잇수 24개)와 기어 B가 맞물려 있다.\n기어 A가 1분에 15번 회전할 때 기어 B는 20번 회전한다.\n(잇수×회전수=일정)\n① 기어 B의 잇수를 구하시오.\n② 기어 A의 잇수를 x, 회전수를 y라 할 때 관계식을 쓰시오.\n③ 기어 A 잇수가 30개로 바뀌면 회전수는?',
      answer: '① 18개  ② y=360/x  ③ 12번',
      hint: '잇수×회전수=일정. 24×15=360이 비례상수.',
      steps: [
        '① 24×15 = B잇수×20 → B잇수=360/20=18',
        '② 잇수×회전수=360 → y=360/x',
        '③ x=30: y=360/30=12 (번)',
      ],
    },
    misconceptions: ['FP02'],
  },
]

// ── 하위 컴포넌트 ────────────────────────────────────────────────────────────
function PracticePanel({ practice }: { practice: typeof STEPS[0]['practice'] }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-blue-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3 whitespace-pre-line">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 28)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800 whitespace-pre-line">{example.question}</p>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs font-bold text-blue-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-blue-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic whitespace-pre-line">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story }: { story: StepStory }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-blue-100 text-blue-800',
    teacher: 'bg-sky-100 text-sky-800',
    friend: 'bg-indigo-100 text-indigo-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-blue-50 to-white border border-blue-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-blue-100 rounded-full">
          <div className="h-1 bg-blue-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      {!revealed ? (
        <div className="flex gap-2">
          {lineIdx < story.lines.length - 1 && (
            <button onClick={() => setLineIdx(lineIdx + 1)}
              className="flex-1 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold rounded-xl text-xs transition-colors">
              다음 대화 →
            </button>
          )}
          {lineIdx === story.lines.length - 1 && (
            <button onClick={() => setRevealed(true)}
              className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-1">
              <Sparkles size={12} /> 핵심 공식 보기
            </button>
          )}
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
          <p className="text-lg text-center mb-1">{story.revealEmoji}</p>
          <p className="text-xs text-blue-800 font-mono text-center">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id)).slice(0, 3)
  if (visible.length === 0) return null
  return (
    <div className="mb-4 card border border-amber-200 bg-amber-50">
      <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
        <Brain size={12} /> AI 예상 취약 개념
      </p>
      <div className="space-y-1.5">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold truncate ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 truncate">{f.fragment.fixTip.slice(0, 40)}...</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0"><X size={11} /></button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
const TOTAL_STEPS = STEPS.length

interface F1_1LearnPageProps { defaultStepId?: string | null }

export default function F1_1LearnPage({ defaultStepId }: F1_1LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(null)
  const [activeTab, setActiveTab] = useState<'story' | 'concept' | 'examples' | 'practice'>('story')

  const { completeStep } = useProgressStore(s => ({ completeStep: s.completeStep }))
  const learnProgress = useLearnProgress(UNIT_ID)
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const completedCount = learnProgress.completedCount
  const progressPct = learnProgress.pct

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) { setSelectedStep(step); setActiveTab('concept') }
    }
  }, [defaultStepId])

  const handleComplete = (step: typeof STEPS[0]) => {
    completeStep(UNIT_ID, step.id, step.title)
    setSelectedStep(null)
  }

  // ── 단계 목록 화면 ──────────────────────────────────────────────────────────
  if (!selectedStep) {
    return (
      <div className="p-4 space-y-4">
        <PredictedFragmentPanel fragments={predictedFragments} />

        {/* 헤더 */}
        <div className="card bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-2xl">📈</div>
            <div>
              <h2 className="text-lg font-black text-slate-800">정비례와 반비례</h2>
              <p className="text-xs text-slate-500">F1-1 · 함수 영역</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-2 bg-blue-500 rounded-full transition-all"
                style={{ width: `${progressPct}%` }} />
            </div>
            <span className="text-xs font-bold text-blue-600 w-16 text-right">
              {completedCount}/{TOTAL_STEPS} 완료
            </span>
          </div>
        </div>

        {/* 단계 목록 */}
        <div className="space-y-2">
          {STEPS.map((step) => {
            const stepProg = learnProgress.steps[step.id]
            const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
            const video = getApprovedByTag(step.id)
            return (
              <div key={step.id}
                className={`card border-2 cursor-pointer transition-all hover:shadow-md ${done ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200 hover:border-blue-300'}`}
                onClick={() => { setSelectedStep(step); setActiveTab('story') }}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${done ? 'bg-blue-100' : step.lightColor}`}>
                    {done ? '✅' : step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-800">{step.title}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${step.difficultyColor}`}>
                        {step.difficulty}
                      </span>
                      {video && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold flex items-center gap-0.5">
                          <Youtube size={9} /> 영상
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{step.subtitle}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-slate-400">{step.id}</span>
                    <ChevronRight size={15} className="text-slate-300" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {completedCount === TOTAL_STEPS && (
          <div className="card bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-300 text-center">
            <div className="text-3xl mb-2">🎉</div>
            <p className="text-sm font-bold text-blue-700">모든 개념 학습 완료!</p>
            <p className="text-xs text-slate-500 mt-1">실전 TEST에 도전해보세요</p>
          </div>
        )}
      </div>
    )
  }

  // ── 단계 상세 화면 ──────────────────────────────────────────────────────────
  const step = selectedStep
  const stepProg = learnProgress.steps[step.id]
  const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
  const story = STEP_STORIES[step.id]
  const video = getApprovedByTag(step.id)

  const tabs = [
    { id: 'story' as const, label: '스토리', icon: <MessageCircle size={13} /> },
    { id: 'concept' as const, label: '개념', icon: <BookOpen size={13} /> },
    { id: 'examples' as const, label: '예제', icon: <FileText size={13} /> },
    { id: 'practice' as const, label: '연습', icon: <PenTool size={13} /> },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* 상단 헤더 */}
      <div className={`${step.lightColor} border-b ${step.borderColor} px-4 pt-3 pb-0 flex-shrink-0`}>
        <button onClick={() => setSelectedStep(null)}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-2 transition-colors">
          ← 목록으로
        </button>
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-10 h-10 rounded-xl ${step.color} flex items-center justify-center text-white text-xl flex-shrink-0`}>
            {step.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-black text-slate-800">{step.title}</h3>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${step.difficultyColor}`}>
                {step.difficulty}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{step.subtitle}</p>
          </div>
        </div>

        {/* 탭 */}
        <div className="flex gap-1 overflow-x-auto pb-0">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-t-lg transition-colors whitespace-nowrap flex-shrink-0
                ${activeTab === t.id
                  ? `${step.color} text-white`
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 콘텐츠 */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* 태그 */}
        <div className="flex flex-wrap gap-1 mb-3">
          {step.tags.map(tag => (
            <span key={tag} className={`text-xs px-2 py-0.5 rounded-full ${step.lightColor} ${step.textColor} font-medium`}>
              #{tag}
            </span>
          ))}
        </div>

        {/* 학습 목표 */}
        <div className={`flex items-start gap-2 p-3 ${step.lightColor} border ${step.borderColor} rounded-xl mb-4`}>
          <Target size={14} className={step.textColor + ' flex-shrink-0 mt-0.5'} />
          <p className="text-xs text-slate-700">{step.objective}</p>
        </div>

        {/* 영상 버튼 */}
        {video && (
          <button
            onClick={() => setVideoModal({ stepId: step.id, label: step.title })}
            className="w-full mb-4 flex items-center justify-center gap-2 py-2.5 bg-red-50 border border-red-200 rounded-xl text-xs font-semibold text-red-600 hover:bg-red-100 transition-colors">
            <Youtube size={14} /> 개념 영상 보기
          </button>
        )}

        {/* 탭별 콘텐츠 */}
        {activeTab === 'story' && story && <StoryCard story={story} />}

        {activeTab === 'concept' && (
          <div className="space-y-3">
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <p className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1">
                <Lightbulb size={12} /> 핵심 개념
              </p>
              <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                {step.concept}
              </div>
            </div>
            <div className={`${step.lightColor} border ${step.borderColor} rounded-2xl p-4`}>
              <p className={`text-xs font-bold ${step.textColor} mb-2 flex items-center gap-1`}>
                <Star size={12} /> 핵심 포인트
              </p>
              <ul className="space-y-1.5">
                {step.keyPoints.map((kp, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <ArrowRight size={11} className={step.textColor + ' flex-shrink-0 mt-0.5'} />
                    <span className="font-mono">{kp}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="space-y-3">
            {step.examples.map((ex, i) => (
              <ExampleCard key={i} example={ex} idx={i} />
            ))}
          </div>
        )}

        {activeTab === 'practice' && (
          <PracticePanel practice={step.practice} />
        )}

        {/* 완료 버튼 */}
        <div className="mt-6">
          {!done ? (
            <button
              onClick={() => handleComplete(step)}
              className={`w-full py-3 ${step.color} text-white font-bold rounded-2xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity`}>
              <CheckCircle size={16} /> 학습 완료
            </button>
          ) : (
            <div className="flex items-center justify-center gap-2 py-3 text-blue-600">
              <CheckCircle size={16} />
              <span className="text-sm font-semibold">완료된 단계입니다</span>
            </div>
          )}
        </div>
      </div>

      {/* 영상 모달 */}
      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
